import React, { forwardRef, ReactNode, RefAttributes } from 'react';
import { MixProps } from '@visalex/core';

import cx from 'clsx';

import { BaseFieldProps } from '../../Form/utils/connectFields/connectFields.utils';

import { Postfix } from './components/Addons/Postfix/Postfix';
import { Prefix } from './components/Addons/Prefix/Prefix';

import styles from './Input.module.scss';

/**
 * Default values that could be filled into <input />
 */

export type InputProps<Val = string> = MixProps<
  BaseFieldProps<Val> & {
    /**
     * Validity of the field: valid=false = invalid, valid=null|undefined = not yet validated
     */
    valid?: boolean | null;

    prefixContent?: ReactNode;
    postfixContent?: ReactNode;
    prefixClassName?: string;
    postfixClassName?: string;
  } & RefAttributes<HTMLInputElement>,
  HTMLInputElement
>;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      className,
      valid,
      prefixContent,
      prefixClassName,
      postfixClassName,
      postfixContent,
      disabled,
      readOnly,
      onChange,
      ...props
    },
    ref
  ) => {
    if (props.placeholder) props['aria-label'] = props.placeholder;

    return (
      <div
        className={cx(
          className,
          styles['input-box'],
          (disabled || readOnly) && styles['input-box--disabled'],
          typeof valid === 'boolean' && styles[`input-box--${valid ? 'valid' : 'invalid'}`]
        )}
      >
        <Prefix content={prefixContent} className={prefixClassName} />
        <input
          data-testid="input-component"
          {...props}
          ref={ref}
          onChange={(e) => {
            onChange && onChange(e.target.value, e);
          }}
          className={styles.input}
          type={type}
          readOnly={readOnly}
          disabled={disabled}
        />
        <Postfix content={postfixContent} className={postfixClassName} />
      </div>
    );
  }
);

Input.displayName = 'Input';
